const BULK_DELETE_MAX = 200;

export const DOCUMENT_AI_BATCH_BASE_URL =
  import.meta.env.VITE_OCR_CLIENT_BASE_URL ||
  'https://rx-ocr-dev-448580105039.asia-northeast3.run.app';

export const DOCUMENT_AI_TASK_BATCH_BASE_URL =
  import.meta.env.VITE_OCR_TASK_CLIENT_BASE_URL ||
  'https://rx-ocr-task-queue-448580105039.asia-northeast3.run.app';

export const getDocumentAiBatchBaseUrl = ({ useTaskQueueClient = false } = {}) =>
  useTaskQueueClient ? DOCUMENT_AI_TASK_BATCH_BASE_URL : DOCUMENT_AI_BATCH_BASE_URL;

export const collectOrderedUniqueJobIds = (selectedKeys: unknown[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of selectedKeys ?? []) {
    const id = String(x ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

export const getBulkDeleteJobIdLimit = () => BULK_DELETE_MAX;

export const extractBulkDeleteItemsFromResponseBody = (body: unknown) => {
  if (body == null || typeof body !== 'object') return [];
  const items = (body as { items?: unknown[] }).items;
  return Array.isArray(items) ? items.filter(Boolean) : [];
};

export const getJobIdFromDeleteResultItem = (item: Record<string, unknown>) => {
  const v = item?.jobId ?? item?.id;
  return v != null ? String(v).trim() : '';
};

const isTruthyNotFound = (value: unknown) => {
  if (value === true) return true;
  return typeof value === 'string' && value.toLowerCase() === 'true';
};

export const isDocumentAiBatchJobDeleteItemNotFound = (item: Record<string, unknown>) =>
  isTruthyNotFound(item.notFound);

const hasMeaningfulError = (value: unknown) => {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
};

export const isDocumentAiBatchJobDeleteItemSuccess = (item: Record<string, unknown>) => {
  if (!item || typeof item !== 'object') return false;
  if (isTruthyNotFound(item.notFound)) return false;
  if (hasMeaningfulError(item.error)) return false;
  return true;
};

const readJsonResponse = async (res: Response) => {
  const text = await res.text().catch(() => '');
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = { error: { code: 'INVALID_JSON', detail: String(text ?? '').slice(0, 500) } };
  }
  return { text, body };
};

export const postDocumentAiBatchJobsDelete = async (jobIds: string[]) => {
  const url = `${DOCUMENT_AI_BATCH_BASE_URL}/document-ai/batch/jobs/delete`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobIds }),
  });
  const { body } = await readJsonResponse(res);
  return { ok: res.ok, status: res.status, body };
};

export const deleteDocumentAiBatchJobById = async (jobId: string) => {
  const id = String(jobId ?? '').trim();
  const url = `${DOCUMENT_AI_BATCH_BASE_URL}/document-ai/batch/jobs/${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: 'DELETE', headers: { Accept: 'application/json' } });
  const { body } = await readJsonResponse(res);
  const itemSuccess =
    res.ok &&
    (body == null ||
      typeof body !== 'object' ||
      isDocumentAiBatchJobDeleteItemSuccess(body as Record<string, unknown>));

  return {
    httpOk: res.ok,
    status: res.status,
    body,
    success: itemSuccess,
    notFound:
      body && typeof body === 'object'
        ? isTruthyNotFound((body as Record<string, unknown>).notFound)
        : false,
    error: body && typeof body === 'object' ? (body as Record<string, unknown>).error : undefined,
  };
};
